import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Phone, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface ReconnectionPromptProps {
  isVisible: boolean;
  isReconnecting: boolean;
  onReconnect: () => Promise<boolean>;
  onCancel: () => void;
  estimatedProgress: number;
  disconnectionCount: number;
}

const ReconnectionPrompt: React.FC<ReconnectionPromptProps> = ({
  isVisible,
  isReconnecting,
  onReconnect,
  onCancel,
  estimatedProgress,
  disconnectionCount
}) => {
  const [countdown, setCountdown] = useState(10);
  
  // Auto-reconnect countdown
  useEffect(() => {
    if (!isVisible || isReconnecting) return;
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          onReconnect();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isVisible, isReconnecting, onReconnect]);
  
  if (!isVisible) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          <Card className="max-w-md w-full p-6 text-center">
            <div className="mb-4">
              <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold">Call Disconnected</h3>
              <p className="text-muted-foreground mt-1">
                Don't worry - we can continue exactly where you left off
                {disconnectionCount > 0 && (
                  <span className="block text-xs mt-1 opacity-75">
                    {disconnectionCount === 1 ? 'Second disconnection' : `${disconnectionCount + 1} disconnections`}
                  </span>
                )}
              </p>
            </div>
            
            <div className="mb-6">
              <div className="bg-gray-100 rounded-full h-2 mb-2">
                <motion.div 
                  className="bg-blue-500 h-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${estimatedProgress * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                About {Math.round(estimatedProgress * 100)}% complete
              </p>
            </div>
            
            <div className="space-y-3">
              <Button 
                onClick={onReconnect} 
                disabled={isReconnecting}
                className="w-full"
                size="lg"
              >
                {isReconnecting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Reconnecting...
                  </>
                ) : (
                  <>
                    <Phone className="w-4 h-4 mr-2" />
                    Continue Call Now
                  </>
                )}
              </Button>
              
              {!isReconnecting && (
                <motion.p 
                  className="text-xs text-muted-foreground"
                  key={countdown}
                  initial={{ scale: 1.1 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", damping: 10, stiffness: 100 }}
                >
                  Auto-reconnecting in {countdown} seconds...
                </motion.p>
              )}
              
              <Button 
                variant="ghost" 
                onClick={onCancel}
                disabled={isReconnecting}
                className="w-full"
              >
                Start Over Instead
              </Button>
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ReconnectionPrompt; 